import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  CheckCircle,
  XCircle,
  Eye,
  PencilSimple,
  Robot,
  MagnifyingGlass,
  CaretLeft,
  CaretRight,
  ListChecks,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 50;

// Fields that should be edited as a single-line input (short SEO meta).
const SHORT_FIELDS = new Set(['meta_title', 'meta_description', 'focus_keyword', 'product_name', 'name']);
const LENGTH_HINTS = { meta_title: 60, meta_description: 155, focus_keyword: 60 };

const FIELD_LABELS = {
  meta_title: 'Meta-tittel',
  meta_description: 'Meta-beskrivelse',
  focus_keyword: 'Fokusnøkkelfrase',
  short_description: 'Kort beskrivelse',
  description: 'Produktbeskrivelse',
  product_name: 'Varenavn',
  name: 'Navn',
  translate_danish: 'Oversettelse (NB)',
  delivery_info: 'Leveringsinfo',
  image_alt_text: 'Alt-tekst bilder',
  tags: 'Stikkord',
};

function stripHtml(html) {
  if (!html || typeof html !== 'string') return '';
  return html.replace(/<[^>]*>/g, '').trim();
}

/**
 * ApprovalQueue – paginated, self-fetching approval list.
 *
 * Backend pagination is mandatory: when the queue grew past ~500 items,
 * rendering all of them at once crashed the production tab.
 *
 * Props:
 *   kind:        'product' | 'category' | 'term'  (required — server-side filter)
 *   getHeaders:  () => headers
 *   onChanged:   () => void   parent refresh callback (status counters, logs, etc.)
 *   refreshKey:  number — bump to force a refetch (e.g. after AutoGuard ticks)
 *   emptyHint:   optional text shown when filter matches nothing
 */
export default function ApprovalQueue({ kind, getHeaders, onChanged, refreshKey, emptyHint }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [edits, setEdits] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [editing, setEditing] = useState({});

  const onChangedRef = useRef(onChanged);
  useEffect(() => { onChangedRef.current = onChanged; }, [onChanged]);

  // Debounce search input to avoid hammering backend while typing.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to page 0 when filters change.
  useEffect(() => { setPage(0); }, [debouncedSearch, kind]);

  const fetchPage = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: String(page * PAGE_SIZE),
        limit: String(PAGE_SIZE),
        kind,
      });
      if (debouncedSearch) params.set('search', debouncedSearch);
      const { data } = await axios.get(
        `${API}/api/autoguard/approval-queue?${params.toString()}`,
        { headers: getHeaders() },
      );
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch {
      // Soft-fail — keep last page rather than crashing the tab.
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, kind, getHeaders]);

  useEffect(() => { fetchPage(); }, [fetchPage, refreshKey]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const allIds = useMemo(() => items.map((i) => i.approval_id), [items]);
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));
  const someSelected = selected.size > 0 && !allSelected;

  const toggleSelectAll = () => {
    if (allSelected) {
      const next = new Set(selected);
      allIds.forEach((id) => next.delete(id));
      setSelected(next);
    } else {
      setSelected(new Set([...selected, ...allIds]));
    }
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const setField = (approvalId, field, value) => {
    setEdits((prev) => ({
      ...prev,
      [approvalId]: { ...(prev[approvalId] || {}), [field]: value },
    }));
  };
  const togglePreview = (key) => setEditing((p) => ({ ...p, [key]: !p[key] }));

  const buildEditedChanges = (item) => {
    const original = item.changes || {};
    const myEdits = edits[item.approval_id] || {};
    const merged = {};
    Object.keys(original).forEach((k) => {
      const raw = myEdits[k] !== undefined ? myEdits[k] : original[k];
      if (k === 'tags' && typeof raw === 'string') {
        merged[k] = raw.split(',').map((s) => s.trim()).filter(Boolean);
      } else {
        merged[k] = raw;
      }
    });
    return merged;
  };

  const refreshAndNotify = async () => {
    await fetchPage();
    onChangedRef.current && onChangedRef.current();
  };

  const approveOne = async (item) => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/autoguard/approve/${item.approval_id}`,
        { changes: buildEditedChanges(item) },
        { headers: getHeaders() },
      );
      toast.success('Godkjent og pushet til WooCommerce');
      setSelected((prev) => { const n = new Set(prev); n.delete(item.approval_id); return n; });
      setEdits((prev) => { const c = { ...prev }; delete c[item.approval_id]; return c; });
      await refreshAndNotify();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Godkjenning feilet');
    } finally { setBusy(false); }
  };

  const rejectOne = async (item) => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/autoguard/reject/${item.approval_id}`,
        {},
        { headers: getHeaders() },
      );
      toast.success('Avvist');
      setSelected((prev) => { const n = new Set(prev); n.delete(item.approval_id); return n; });
      await refreshAndNotify();
    } catch {
      toast.error('Avvisning feilet');
    } finally { setBusy(false); }
  };

  const bulkApproveSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Godkjenn og push ${selected.size} endring(er) til WooCommerce?`)) return;
    setBusy(true);
    try {
      const payload = {
        items: items
          .filter((it) => selected.has(it.approval_id))
          .map((it) => ({ approval_id: it.approval_id, changes: buildEditedChanges(it) })),
      };
      const { data } = await axios.post(
        `${API}/api/autoguard/bulk-approve`,
        payload,
        { headers: getHeaders() },
      );
      toast.success(data.message || `${data.queued_count} endringer behandles i bakgrunnen`);
      setSelected(new Set());
      setEdits({});
      await refreshAndNotify();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk-godkjenning feilet');
    } finally { setBusy(false); }
  };

  const bulkRejectSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Avvis ${selected.size} endring(er)?`)) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/api/autoguard/bulk-reject`,
        { approval_ids: Array.from(selected) },
        { headers: getHeaders() },
      );
      toast.success(data.message || `${data.queued_count} avvises i bakgrunnen`);
      setSelected(new Set());
      await refreshAndNotify();
    } catch {
      toast.error('Bulk-avvisning feilet');
    } finally { setBusy(false); }
  };

  const approveAllMatching = async () => {
    if (total === 0) return;
    const scopeLabel = debouncedSearch ? `${total} treff på "${debouncedSearch}"` : `alle ${total} elementer`;
    if (!window.confirm(`Godkjenn og push ${scopeLabel} på tvers av alle sider?`)) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/api/autoguard/bulk-approve-all`,
        { kind, search: debouncedSearch || null },
        { headers: getHeaders() },
      );
      toast.success(data.message || `${data.queued_count} endringer behandles i bakgrunnen`);
      setSelected(new Set());
      setEdits({});
      await refreshAndNotify();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Godkjenn-alle feilet');
    } finally { setBusy(false); }
  };

  const rejectAllMatching = async () => {
    if (total === 0) return;
    const scopeLabel = debouncedSearch ? `${total} treff på "${debouncedSearch}"` : `alle ${total} elementer`;
    if (!window.confirm(`Avvis ${scopeLabel} på tvers av alle sider? (Kan ikke angres bulk).`)) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/api/autoguard/bulk-reject-all`,
        { kind, search: debouncedSearch || null },
        { headers: getHeaders() },
      );
      toast.success(data.message || `${data.queued_count} avvises i bakgrunnen`);
      setSelected(new Set());
      await refreshAndNotify();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Avvis-alle feilet');
    } finally { setBusy(false); }
  };

  // Empty + nothing matching filter
  if (!loading && total === 0 && !debouncedSearch) {
    return (
      <Card className="border border-border">
        <CardContent className="p-8 text-center text-muted-foreground">
          <Robot size={32} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">{emptyHint || 'Ingen endringer venter på godkjenning'}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="approval-queue">
      {/* Search + total count */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="approval-search-row">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Søk produkt/kategori/term..."
            className="pl-9"
            data-testid="approval-search-input"
          />
        </div>
        <Badge variant="outline" className="text-xs" data-testid="approval-total-badge">
          {total} totalt{debouncedSearch ? ` (filtrert)` : ''}
        </Badge>
      </div>

      {/* Bulk action bar */}
      <div
        className="flex items-center justify-between bg-muted/40 border border-border rounded-md px-4 py-2 sticky top-2 z-10 backdrop-blur-sm gap-3 flex-wrap"
        data-testid="bulk-action-bar"
      >
        <div className="flex items-center gap-3">
          <Checkbox
            checked={allSelected ? true : someSelected ? 'indeterminate' : false}
            onCheckedChange={toggleSelectAll}
            data-testid="select-all-checkbox"
          />
          <span className="text-sm text-muted-foreground">
            {selected.size > 0
              ? `${selected.size} valgt`
              : `Velg synlige (${items.length})`}
          </span>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            onClick={bulkRejectSelected}
            disabled={selected.size === 0 || busy}
            className="gap-1 text-destructive"
            data-testid="bulk-reject-selected-btn"
          >
            <XCircle size={14} /> Avvis valgte
          </Button>
          <Button
            size="sm"
            onClick={bulkApproveSelected}
            disabled={selected.size === 0 || busy}
            className="gap-1"
            data-testid="bulk-approve-selected-btn"
          >
            <CheckCircle size={14} /> Godkjenn valgte
          </Button>
          <div className="w-px h-6 bg-border mx-1" aria-hidden />
          <Button
            size="sm"
            variant="outline"
            onClick={rejectAllMatching}
            disabled={total === 0 || busy}
            className="gap-1 text-destructive"
            title="Avvis alle elementer i køen (alle sider)"
            data-testid="bulk-reject-all-btn"
          >
            <ListChecks size={14} /> Avvis alle ({total})
          </Button>
          <Button
            size="sm"
            onClick={approveAllMatching}
            disabled={total === 0 || busy}
            className="gap-1"
            title="Godkjenn alle elementer i køen (alle sider)"
            data-testid="bulk-approve-all-btn"
          >
            <ListChecks size={14} /> Godkjenn alle ({total})
          </Button>
        </div>
      </div>

      {loading && items.length === 0 && (
        <Card className="border border-border">
          <CardContent className="p-8 text-center text-muted-foreground text-sm">
            Laster godkjenningskø…
          </CardContent>
        </Card>
      )}

      {!loading && items.length === 0 && debouncedSearch && (
        <Card className="border border-border">
          <CardContent className="p-8 text-center text-muted-foreground text-sm">
            Ingen treff på "{debouncedSearch}".
          </CardContent>
        </Card>
      )}

      {items.map((item) => {
        const isSelected = selected.has(item.approval_id);
        const itemEdits = edits[item.approval_id] || {};
        const editedFields = Object.keys(itemEdits).filter(
          (k) => itemEdits[k] !== item.changes?.[k],
        );

        return (
          <Card
            key={item.approval_id}
            className={`border transition-colors ${isSelected ? 'border-primary' : 'border-border'}`}
            data-testid={`approval-${item.approval_id}`}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3 gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleOne(item.approval_id)}
                    className="mt-1"
                    data-testid={`select-${item.approval_id}`}
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{item.product_name}</p>
                    <div className="flex gap-1 mt-1 flex-wrap">
                      {item.product_type && <Badge variant="outline" className="text-xs">{item.product_type}</Badge>}
                      <Badge variant="secondary" className="text-xs">
                        {Object.keys(item.changes || {}).length} felt
                      </Badge>
                      {editedFields.length > 0 && (
                        <Badge variant="default" className="text-xs gap-1">
                          <PencilSimple size={10} /> {editedFields.length} redigert
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => rejectOne(item)}
                    disabled={busy}
                    className="gap-1 text-destructive"
                    data-testid={`reject-${item.approval_id}`}
                  >
                    <XCircle size={12} /> Avvis
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => approveOne(item)}
                    disabled={busy}
                    className="gap-1"
                    data-testid={`approve-${item.approval_id}`}
                  >
                    <CheckCircle size={12} /> Godkjenn & Push
                  </Button>
                </div>
              </div>

              {/* Per-field editor */}
              <div className="space-y-3 mt-3">
                {Object.entries(item.changes || {}).map(([field, aiValue]) => {
                  const isTags = field === 'tags';
                  const aiDisplay = isTags && Array.isArray(aiValue) ? aiValue.join(', ') : aiValue;
                  const editedValue = itemEdits[field] !== undefined ? itemEdits[field] : aiDisplay;
                  const isShort = SHORT_FIELDS.has(field);
                  const targetLen = LENGTH_HINTS[field];
                  const previewKey = `${item.approval_id}:${field}`;
                  const isEditing = !!editing[previewKey];
                  const charCount = stripHtml(String(editedValue || '')).length;
                  const isHtml = /<[a-z][\s\S]*?>/i.test(String(editedValue || ''));
                  const wasEdited = itemEdits[field] !== undefined && itemEdits[field] !== aiValue;

                  return (
                    <div key={field} className="bg-muted/40 p-3 rounded-md">
                      <div className="flex items-center justify-between mb-2 gap-2">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                          {FIELD_LABELS[field] || field}
                          {wasEdited && (
                            <span className="ml-2 text-primary normal-case font-normal">· redigert</span>
                          )}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className={targetLen && charCount > targetLen ? 'text-warning' : ''}>
                            {charCount}{targetLen ? `/${targetLen}` : ''} tegn
                          </span>
                          <button
                            type="button"
                            onClick={() => togglePreview(previewKey)}
                            className="flex items-center gap-1 hover:text-foreground"
                            data-testid={`edit-toggle-${previewKey}`}
                          >
                            {isEditing ? (
                              <><Eye size={11} /> Forhåndsvis</>
                            ) : (
                              <><PencilSimple size={11} /> Rediger</>
                            )}
                          </button>
                        </div>
                      </div>

                      {item.original?.[field] && (
                        <details className="mb-2">
                          <summary className="text-xs text-destructive cursor-pointer hover:underline">
                            Vis original
                          </summary>
                          <div
                            className="mt-1 p-2 bg-destructive/5 border border-destructive/10 rounded text-xs text-muted-foreground prose prose-xs max-w-none"
                            dangerouslySetInnerHTML={{ __html: String(item.original[field]) }}
                          />
                        </details>
                      )}

                      {!isEditing ? (
                        isHtml ? (
                          <div
                            className="p-3 bg-success/5 border border-success/20 rounded prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: String(editedValue || '') }}
                            data-testid={`preview-${previewKey}`}
                          />
                        ) : (
                          <div
                            className="p-3 bg-success/5 border border-success/20 rounded text-sm whitespace-pre-wrap"
                            data-testid={`preview-${previewKey}`}
                          >
                            {String(editedValue || '')}
                          </div>
                        )
                      ) : isShort ? (
                        <Input
                          value={String(editedValue ?? '')}
                          onChange={(e) => setField(item.approval_id, field, e.target.value)}
                          className="bg-background"
                          autoFocus
                          data-testid={`edit-${previewKey}`}
                        />
                      ) : (
                        <Textarea
                          rows={8}
                          value={String(editedValue ?? '')}
                          onChange={(e) => setField(item.approval_id, field, e.target.value)}
                          className={`bg-background ${isHtml ? 'font-mono text-xs' : ''}`}
                          autoFocus
                          data-testid={`edit-${previewKey}`}
                        />
                      )}
                    </div>
                  );
                })}

                {item.context && Object.keys(item.context).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-dashed border-border/60">
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2">
                      Til referanse (uendret)
                    </p>
                    <div className="space-y-2">
                      {Object.entries(item.context).map(([field, value]) => (
                        <div key={`ctx-${field}`} className="bg-muted/20 p-3 rounded-md border border-border/40">
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                            {FIELD_LABELS[field] || field} · gjeldende
                          </p>
                          {/<[a-z][\s\S]*?>/i.test(String(value || '')) ? (
                            <div
                              className="p-2 bg-background/50 rounded text-xs prose prose-xs max-w-none"
                              dangerouslySetInnerHTML={{ __html: String(value || '') }}
                            />
                          ) : (
                            <div className="p-2 bg-background/50 rounded text-xs whitespace-pre-wrap">
                              {String(value || '') || <span className="italic text-muted-foreground">Tom</span>}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Pagination */}
      {totalPages > 1 && (
        <div
          className="flex items-center justify-between bg-muted/30 border border-border rounded-md px-4 py-2"
          data-testid="approval-pagination"
        >
          <span className="text-xs text-muted-foreground">
            Side {page + 1} av {totalPages} · viser {items.length} av {total}
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="gap-1"
              data-testid="approval-prev-page"
            >
              <CaretLeft size={12} /> Forrige
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="gap-1"
              data-testid="approval-next-page"
            >
              Neste <CaretRight size={12} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
